import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { supportedMetricsSelector, bleEnabledSelector } from '@store/common/selectors';
import { useViewerStore } from '@store/viewerModule/base';
import { Retry } from '@components/common';
import { isEmpty } from 'lodash';
import MetricGroup from './MetricGroup';
import { StyledSummary, MetricDivider, ErrorMessage } from './styled';
import { checkAdvancedMetricsEnabled, buildSummary } from '../common/utils';
import { viewerMetricList, viewersMetricCategory } from '../common/mappings';
import useFetchSummaryData from './usefetchSummaryData';
import { metricReqBody, metricLoading } from '../AISummary/atom';
import { useSetAtom } from 'jotai';

const ERROR_MSG =
  'This viewer has high cardinality of Devices >= 201. Try reducing the query time range.';

function Summary() {
  const isBusinessMetricEnabled = useSelector(bleEnabledSelector);
  const supportedMetrics = useSelector(supportedMetricsSelector);
  const [
    {
      interval: { isLive },
    },
  ] = useViewerStore();
  const isAdvancedMetricEnabled = checkAdvancedMetricsEnabled(supportedMetrics);
  const metricsList = viewerMetricList(isBusinessMetricEnabled, isAdvancedMetricEnabled);
  const metricCategories = viewersMetricCategory(isBusinessMetricEnabled, isAdvancedMetricEnabled);
  const { isIdle, status, data, refetch, isError, error } = useFetchSummaryData(metricsList);
  const setMetricReqBody = useSetAtom(metricReqBody);
  const setMetricLoading = useSetAtom(metricLoading);

  const checkCardinalityError = status === 'error' && isError && error?.message.includes(500);
  const summary = data ? data.data[0].totals : null;
  const reqBody = data?.data?.[0].reqBody || null;

  useEffect(() => {
    if (checkCardinalityError) {
      setMetricReqBody({
        error: ERROR_MSG,
      });
    } else {
      setMetricReqBody(reqBody);
    }
  }, [reqBody, setMetricReqBody, checkCardinalityError]);

  useEffect(() => {
    setMetricLoading(status === 'loading' || isIdle);
  }, [status, setMetricLoading, isIdle]);

  const summaryData = React.useMemo(
    () => buildSummary(metricCategories, summary),
    [metricCategories, summary],
  );

  if (isLive) {
    return (
      <StyledSummary>
        <ErrorMessage>
          Summary data is not applicable for Live Mode
          <Tooltip title="Live mode shows only Active sessions but Summary represents all sessions. So, the data points may not match.">
            <InfoCircleOutlined style={{ marginLeft: 6 }} />
          </Tooltip>
        </ErrorMessage>
      </StyledSummary>
    );
  }

  if (checkCardinalityError) {
    return (
      <StyledSummary>
        <ErrorMessage>{ERROR_MSG}</ErrorMessage>
      </StyledSummary>
    );
  }
  
  return (
    <StyledSummary>
      <Retry
        isLoading={status === 'loading' || isIdle}
        showError={status === 'error'}
        isEmpty={isEmpty(summaryData)}
        mountChildAlways={false}
        fetchData={refetch}
        horizontal={true}
      >
        <MetricGroup metricGroup={summaryData.PLAY_GROUP} />
        <MetricDivider />
        <MetricGroup metricGroup={summaryData.SPI} />
        <MetricDivider />
        <MetricGroup metricGroup={summaryData.ERROR_GROUP} />
        <MetricDivider />
        <MetricGroup metricGroup={summaryData.QUALITY_GROUP} />
      </Retry>
    </StyledSummary>
  );
}

export default React.memo(Summary);
